/**
 * @file api-channel-listings.ts
 * @description API-клиент карточек товара по каналам (phase17.1).
 *
 * Оверлей с наследованием (C1-C6): один Product = источник правды, на канал —
 * оверлей ProductChannelListing. Заменяет прежний api-channel-visibility.
 * Эндпоинты — /admin/catalog/products/:productId/channels.
 */

import { apiFetch } from "./api-client";

export type SalesChannelKind =
  | "WEBSITE" | "VK" | "TELEGRAM" | "MAX" | "OZON" | "WILDBERRIES" | "AVITO";
export type ChannelType = "STOREFRONT" | "MARKETPLACE";
export type FieldOrigin = "inherited" | "overridden";

/** Сводка канала для списка-плиток в редакторе товара. */
export interface ChannelSummary {
  channel: SalesChannelKind;
  channelType: ChannelType;
  isEnabled: boolean;
  hasListing: boolean;
  isPublished: boolean;
  overrideCount: number;
  syncStatus: string | null;
  moderationStatus: string | null;
  needsResync: boolean;
}

/** Поле с пометкой источника (наследуется/переопределено). */
export interface ResolvedField<T> {
  value: T;
  origin: FieldOrigin;
}

export interface ChannelCardMedia {
  id: string;
  type: "IMAGE" | "VIDEO" | "DOC";
  fileKey: string;
  fileMimeType: string;
  sizeBytes: number;
  isPrimary: boolean;
  sortOrder: number;
  alt: string | null;
}

export interface ChannelCardLegal {
  composition: string | null;
  shelfLifeDays: number | null;
  storageConditions: string | null;
  allergens: string[];
  nutritionFacts: unknown;
  badWarning: boolean;
  ageRestriction: number | null;
  notForConsumption: boolean;
  producerId: string | null;
  declarationNumber: string | null;
}

export interface ChannelCardMarketplace {
  channelCategory: ResolvedField<Record<string, unknown> | null>;
  channelAttributes: ResolvedField<Record<string, unknown> | null>;
  externalItemId: string | null;
  syncStatus: string;
  moderationStatus: string;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export interface SerializedPrice {
  value: number | null;
  origin: FieldOrigin;
  minPrice: number | null;
  basePrice: number | null;
}

/** Ручные бейджи витрины (phase18.3, promo.md P4). */
export type ManualBadgeKind = "BESTSELLER" | "COMING_SOON" | "SALE";

/** Эффективная карточка канала (COALESCE оверлей→база). */
export interface ChannelCard {
  channel: SalesChannelKind;
  channelType: ChannelType;
  isPublished: boolean;
  displayName: ResolvedField<string>;
  shortDescription: ResolvedField<string | null>;
  description: ResolvedField<string | null>;
  legal: ChannelCardLegal;
  media: { source: "channel" | "product"; items: ChannelCardMedia[] };
  marketplace: ChannelCardMarketplace | null;
  needsResync: boolean;
  serializedPrice: SerializedPrice;
  /** phase18.3 (P4) — текущий ручной бейдж витрины (сырое значение листинга). */
  manualBadge: ManualBadgeKind | null;
  badgeUntil: string | null;
}

/** Overridable-поля для upsert (neverOverride отклоняются сервером, C2). */
export interface UpsertChannelListingInput {
  isPublished?: boolean;
  displayName?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  priceOverride?: number | null;
  minPrice?: number | null;
  channelCategory?: Record<string, unknown> | null;
  channelAttributes?: Record<string, unknown> | null;
  /** phase18.3 (P4) — null снимает бейдж; BESTSELLER гейтится сервером (≥10 оплаченных заказов). */
  manualBadge?: ManualBadgeKind | null;
  /** Опциональный срок действия бейджа — по истечении снимается автоматически. */
  badgeUntil?: string | null;
}

const base = (productId: string) =>
  `/admin/catalog/products/${encodeURIComponent(productId)}/channels`;

export const channelListings = {
  list: (productId: string) => apiFetch<ChannelSummary[]>(base(productId)),

  card: (productId: string, channel: string) =>
    apiFetch<ChannelCard>(`${base(productId)}/${encodeURIComponent(channel)}`),

  upsert: (productId: string, channel: string, input: UpsertChannelListingInput) =>
    apiFetch<ChannelCard>(`${base(productId)}/${encodeURIComponent(channel)}`, {
      method: "PUT",
      json: input,
    }),

  togglePublish: (productId: string, channel: string, isPublished: boolean) =>
    apiFetch<ChannelCard>(
      `${base(productId)}/${encodeURIComponent(channel)}/publish`,
      { method: "PATCH", json: { isPublished } },
    ),

  reset: (productId: string, channel: string, fields: string[]) =>
    apiFetch<ChannelCard>(`${base(productId)}/${encodeURIComponent(channel)}/reset`, {
      method: "POST",
      json: { fields },
    }),
};

/** Конфигурация канала магазина (phase19.5) — для фильтров списка товаров. */
export interface ShopChannel {
  channel: SalesChannelKind;
  channelType: ChannelType;
  isEnabled: boolean;
}

/** phase19.5 — каналы продаж магазина (GET /admin/catalog/sales-channels). */
export const shopSalesChannels = {
  list: () => apiFetch<ShopChannel[]>("/admin/catalog/sales-channels"),
};

/** Человекочитаемые названия каналов. */
export const CHANNEL_LABELS: Record<SalesChannelKind, string> = {
  WEBSITE: "Сайт",
  VK: "ВКонтакте",
  TELEGRAM: "Telegram",
  MAX: "MAX",
  OZON: "Ozon",
  WILDBERRIES: "Wildberries",
  AVITO: "Avito",
};

/** Человекочитаемые названия ручных бейджей (promo.md P4). */
export const MANUAL_BADGE_LABELS: Record<ManualBadgeKind, string> = {
  BESTSELLER: "Бестселлер",
  COMING_SOON: "Скоро в продаже",
  SALE: "Распродажа",
};
